import React, { useRef, useEffect, useState } from "react";
import Hls from "hls.js";
import { useLanguage } from "../../contexts/LanguageContext";
import "./VideoPlayer.css";

const VideoPlayer = ({ videoUrl, posterUrl, title, autoplay = false, onEnded, subtitles, videoLanguage }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [availableSubtitles, setAvailableSubtitles] = useState([]);
  const { t, language } = useLanguage();

  // Check which subtitle files exist
  useEffect(() => {
    const checkSubtitles = async () => {
      if (!subtitles) {
        setAvailableSubtitles([]);
        return;
      }

      const available = [];
      
      // Helper function to check if a subtitle file exists
      const checkSubtitleFile = async (url, lang, label) => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
          
          const response = await fetch(url, { 
            method: 'HEAD',
            signal: controller.signal,
            mode: 'cors'
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            return { lang, label, src: url };
          }
        } catch (e) {
          // Silently handle errors (404, 403, network errors, etc.)
          console.log(`Subtitle file not available for ${lang}`);
        }
        return null;
      };
      
      // Check both subtitle files in parallel
      const [deSubtitle, enSubtitle] = await Promise.all([
        subtitles.de ? checkSubtitleFile(subtitles.de, 'de', 'Deutsch') : Promise.resolve(null),
        subtitles.en ? checkSubtitleFile(subtitles.en, 'en', 'English') : Promise.resolve(null)
      ]);
      
      if (deSubtitle) available.push(deSubtitle);
      if (enSubtitle) available.push(enSubtitle);
      
      setAvailableSubtitles(available);
    };

    checkSubtitles();
  }, [subtitles]);


  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;

    setIsLoading(true);
    setError(null);

    // Clean up existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        startLevel: 0,            // Start at lowest quality (stop duplicate loading)
        autoLevelCapping: 0,      // Cap at level 0 (single quality only)
        maxBufferLength: 60,      // Buffer 60 seconds ahead minimum
        maxMaxBufferLength: 600,  // Allow buffering up to 10 minutes
        maxBufferHole: 0.5,       // Handle small gaps in segments
        startFragPrefetch: true   // Prefetch next segment
      });

      hlsRef.current = hls;

      // Function to select audio track
      const selectAudioTrack = () => {
        if (hls.audioTracks && hls.audioTracks.length > 1) {
          console.log('Available audio tracks:', hls.audioTracks);
          console.log('User language preference:', language);
          console.log('Video original language:', videoLanguage);
          
          // Determine which language to use for audio
          let targetLanguage = language; // Default to user preference
          
          // If video's original language is English, always use English audio
          if (videoLanguage === 'en') {
            targetLanguage = 'en';
            console.log('Video is originally in English, using English audio');
          }
          
          // Find track matching the target language
          const preferredTrackIndex = hls.audioTracks.findIndex(track => {
            console.log(`Checking track: ${track.name} (lang: ${track.lang}) against target: ${targetLanguage}`);
            return track.lang === targetLanguage || track.name.toLowerCase().includes(targetLanguage === 'en' ? 'english' : 'deutsch');
          });
          
          console.log('Preferred track index:', preferredTrackIndex);
          
          if (preferredTrackIndex !== -1) {
            hls.audioTrack = preferredTrackIndex;
            console.log(`Selected audio track: ${hls.audioTracks[preferredTrackIndex].name} (${targetLanguage})`);
          } else {
            console.log(`No ${targetLanguage} audio track found, using default`);
          }
        } else {
          console.log(`Audio tracks available: ${hls.audioTracks ? hls.audioTracks.length : 0}`);
        }
      };

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsLoading(false);
        console.log('MANIFEST_PARSED - checking audio tracks');
        console.log('HLS Levels:', hls.levels);
        console.log('Auto level enabled:', hls.autoLevelEnabled);
        selectAudioTrack();

        if (autoplay) {
          video.play().catch(() => {
            // Autoplay prevented - user interaction required
          });
        }
      });

      // Debug buffering behavior
      hls.on(Hls.Events.FRAG_LOADING, (event, data) => {
        console.log('Loading fragment:', data.frag.sn, 'Level:', data.frag.level);
      });

      hls.on(Hls.Events.FRAG_LOADED, (event, data) => {
        console.log('Fragment loaded:', data.frag.sn, 'Buffer length:', hls.media.buffered.length);
        if (hls.media.buffered.length > 0) {
          const bufferedEnd = hls.media.buffered.end(hls.media.buffered.length - 1);
          console.log('Buffered until:', bufferedEnd, 'seconds');
        }
      });

      // Also listen for audio tracks updated event
      hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, () => {
        console.log('AUDIO_TRACKS_UPDATED - checking audio tracks');
        selectAudioTrack();
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        // Log non-fatal errors but don't crash playback
        if (!data.fatal) {
          // Only log warnings for actual issues, not normal buffering
          if (data.details !== 'bufferStalledError' && data.details !== 'bufferNudgeOnStall') {
            console.warn('HLS non-fatal error:', data);
          }
          return;
        }

        // Handle fatal errors
        console.error('HLS fatal error:', data);

        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            // Retry network errors
            console.log('Attempting to recover from network error');
            hls.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            // Recover from media errors
            console.log('Attempting to recover from media error');
            hls.recoverMediaError();
            break;
          default:
            // For other fatal errors, destroy and show error
            console.error('Unrecoverable error, destroying HLS instance');
            hls.destroy();
            setError("Video playback failed");
            setIsLoading(false);
            break;
        }
      });

      hls.loadSource(videoUrl);
      hls.attachMedia(video);

      video.addEventListener("canplay", () => setIsLoading(false));
      video.addEventListener("ended", () => onEnded?.());
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Native HLS support (Safari)
      video.src = videoUrl;
      video.addEventListener("canplay", () => setIsLoading(false));
      video.addEventListener("ended", () => onEnded?.());
    } else {
      setError("HLS video playback is not supported in this browser");
      setIsLoading(false);
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoUrl, autoplay, onEnded, language, videoLanguage]);

  // Handle iOS subtitle track management
  useEffect(() => {
    const video = videoRef.current;
    if (!video || availableSubtitles.length === 0) return;

    const handleLoadedMetadata = () => {
      // Force iOS to recognize subtitle tracks
      setTimeout(() => {
        if (video.textTracks.length > 0) {
          const shouldShowSubtitlesByDefault = videoLanguage && videoLanguage !== language;
          
          if (shouldShowSubtitlesByDefault) {
            // Enable the user's language track
            const userLanguageTrack = Array.from(video.textTracks).find(
              track => track.language === language
            );
            
            if (userLanguageTrack) {
              Array.from(video.textTracks).forEach(track => {
                track.mode = track === userLanguageTrack ? 'showing' : 'disabled';
              });
            }
          }
        }
      }, 100);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [availableSubtitles, videoLanguage, language]);

  // Handle component unmount
  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);

  if (error) {
    return (
      <div className="bg-red-600 text-white p-8 rounded-lg text-center">
        <div className="mb-4">
          <h3 className="text-xl font-bold mb-2">
            {t({ en: "Playback Error", de: "Wiedergabefehler" })}
          </h3>
          <p className="text-gray-300">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            {t({ en: "Retry", de: "Erneut versuchen" })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 bg-gray-900 flex items-center justify-center z-10">
          <div className="text-center text-white">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>{t({ en: "Loading video...", de: "Video wird geladen..." })}</p>
          </div>
        </div>
      )}


      <video
        ref={videoRef}
        controls
        poster={posterUrl}
        className="w-full h-auto"
        preload="metadata"
        playsInline
        crossOrigin="anonymous"
        webkit-playsinline="true"
        x5-playsinline="true"
      >
        {availableSubtitles.map((subtitle) => {
          // Show subtitles by default if video language doesn't match user language
          const shouldShowByDefault = videoLanguage && videoLanguage !== language && subtitle.lang === language;
          
          return (
            <track
              key={subtitle.lang}
              kind="subtitles"
              src={subtitle.src}
              srcLang={subtitle.lang}
              label={subtitle.label}
              default={shouldShowByDefault || subtitle.lang === language}
            />
          );
        })}
        {t({
          en: "Your browser does not support video playback.",
          de: "Ihr Browser unterstützt die Videowiedergabe nicht.",
        })}
      </video>
    </div>
  );
};

export default VideoPlayer;